// gatsby-browser.js

/**
 * Gatsby Browser API Implementation
 * 
 * Production-ready client-side behavior with PostHog analytics integration.
 * 
 * Features:
 * - Store initialization for content and application data
 * - Authentication state management with logout fixes
 * - Cross-tab logout coordination without interfering with fresh logins
 * - PostHog analytics with GDPR-compliant consent management
 * - Dynamic layout selection based on route patterns
 * - Session state restoration during navigation
 * - Stale logout event cleanup to prevent redirect loops
 * - Proper logout page handling without auth checks
 */

import React from "react";
import { createRoot } from "react-dom/client";
import LayoutSidebar from "@/components/layout/sidebar";
import LayoutDefault from "@/components/layout/default";
import { AuthRouter } from "@/components/auth";
import TermsAcceptanceGate from "@/components/terms/TermsAcceptanceGate";
import AnnouncementHost from "@/components/announcements/AnnouncementHost";
import { contentStore } from "@/services/app/content";
import { appStore } from "@/services/app/store";
import { authService, cacheUserProfile } from "@/services/auth";
import { isKnownAuthHost } from "@/services/auth/keycloak-url";
import { LanguageProvider, useLanguage } from "@/components/providers/LanguageContext";
import { StoreStatusProvider } from "@/components/reusable/StoreStatusProvider";
import { PostHogProvider } from "@/components/providers/PostHogProvider";
import { MotionConfig } from "motion/react";
// IntercomProvider moved to LayoutSidebar to only show widget after auth + sidebar render
import { clearRedirectLoopCounter } from '@/utils/session-utils';
import { logger } from '@/utils/logger';
import { crossTabManager } from '@/services/app/CrossTabManager';

const log = logger.router;

// Import styles
import "./src/styles/webfonts.css";
import "./src/styles/tailwind.css";
import "./src/styles/base.css";
import "./src/styles/typography.css";
import "./src/styles/plyr.css";

// CRITICAL: Check if we're on logout page with Keycloak parameters FIRST
if (window.location.pathname === '/logout' || window.location.pathname === '/logout/') {
  const params = new URLSearchParams(window.location.search);

  // Check if we have Keycloak session timeout parameters
  if (params.has('code') || params.has('session_state') || params.has('iss')) {
    log.log('CRITICAL: Detected Keycloak redirect to logout page, stopping all auth operations');

    // Set ALL logout markers immediately to prevent any auth processing
    sessionStorage.setItem('controlledLogout', 'true');
    sessionStorage.setItem('sessionTimeout', 'true');
    sessionStorage.setItem('onLogoutPage', 'true');
    sessionStorage.setItem('skipAuthProcessing', 'true');
    sessionStorage.setItem('logoutReason', 'inactivity');

    // Clear the parameters immediately
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

/**
 * Render on the client instead of hydrating. The build-time HTML is a
 * logged-out placeholder, while the first client render already reads the
 * session cache (pages seed useState from appStore), so hydration always
 * mismatched: React threw #418/#423 and fell back to a full client render
 * anyway. The placeholder HTML still paints until this render replaces it.
 */
export const replaceHydrateFunction = () => (element, container) => {
  createRoot(container).render(element);
};

/**
 * Set up store references when the client is initialized.
 */
export const onClientEntry = () => {
  if (typeof window !== 'undefined') {
    // CRITICAL: Check if we're on logout page with Keycloak parameters FIRST
    if (window.location.pathname === '/logout' || window.location.pathname === '/logout/') {
      const params = new URLSearchParams(window.location.search);

      // Check if we have Keycloak session timeout parameters
      if (params.has('code') || params.has('session_state') || params.has('iss')) {
        log.log('CRITICAL: Detected Keycloak redirect to logout page, stopping all auth operations');

        // IMMEDIATELY clear any token refresh intervals
        if (window.authService && window.authService.tokenRefreshInterval) {
          clearInterval(window.authService.tokenRefreshInterval);
          window.authService.tokenRefreshInterval = null;
          log.log('CRITICAL: Cleared token refresh interval');
        }

        // Set ALL logout markers immediately to prevent any auth processing
        sessionStorage.setItem('controlledLogout', 'true');
        sessionStorage.setItem('sessionTimeout', 'true');
        sessionStorage.setItem('onLogoutPage', 'true');
        sessionStorage.setItem('skipAuthProcessing', 'true');
        sessionStorage.setItem('logoutReason', 'inactivity');

        // Clear the parameters immediately
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    // Make auth service available globally for components
    window.authService = authService;

    // Import PostHog service and make it globally available
    const { postHogService } = require('./src/services/analytics/posthog');
    window.postHogService = postHogService;

    // Check for controlled logout state
    const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
    const onLogoutPage = window.location.pathname === '/logout';

    if (controlledLogout || onLogoutPage) {
      log.log('Logout in progress, skipping store initialization');
      // Still set up cross-tab sync even during logout
      crossTabManager.initialize();
      return; // Return here is OK because we're after the main setup
    }

    // Clear stale logout events on app start
    if (typeof localStorage !== 'undefined') {
      try {
        const existingLogoutEvent = localStorage.getItem('logout_event');
        if (existingLogoutEvent) {
          const event = JSON.parse(existingLogoutEvent);
          // Clear if older than 10 seconds
          if (Date.now() - event.timestamp > 10000) {
            localStorage.removeItem('logout_event');
          }
        }
      } catch (e) {
        localStorage.removeItem('logout_event');
      }
    }

    // Store initialization start time for performance tracking
    const startTime = Date.now();

    // Track initialization status at the outer scope
    let contentInitialized = false;
    let appInitialized = false;

    /**
     * Check if both stores are initialized and trigger ready events
     * @private
     */
    const checkBothStoresInitialized = () => {
      if (contentInitialized && appInitialized) {
        // Trigger combined ready event for components that depend on both stores
        window.dispatchEvent(new CustomEvent('storesReady', {
          detail: {
            success: true
          }
        }));
      }
    };

    /**
     * Enhanced auth check function with better profile handling
     * @private
     * @async
     */
    const checkAuthFirst = async () => {
      try {
        // Skip auth check if we're on the logout page
        if (typeof window !== 'undefined' &&
          (window.location.pathname === '/logout' || window.location.pathname === '/logout/')) {
          log.log('On logout page, skipping auth initialization');
          initializeStores();
          return;
        }

        // Skip auth check for token pages and public pages
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          const hasToken = params.has('token') || params.has('invitation') || params.has('invitationToken');
          const currentPath = window.location.pathname;

          // Skip auth for token pages, only load minimal data needed
          if (hasToken) {
            log.log('Token parameter detected in URL, skipping auth and full store initialization');
            // Only initialize content store (for CMS translations)
            contentStore.initialize().then(() => {
              window.dispatchEvent(new CustomEvent('contentStoreReady', { detail: { success: true } }));
            });
            // Load only countries and hotspotSectors (needed for ValidTokenCard)
            Promise.all([
              appStore.valueListHandler.loadValueList('countries').catch(() => { }),
              appStore.valueListHandler.loadValueList('hotspotSectors').catch(() => { })
            ]).then(() => {
              window.dispatchEvent(new CustomEvent('appStoreReady', { detail: { success: true } }));
            });
            return;
          }

          // Skip auth AND full store init for register page
          // Register page only needs countries, which it loads itself
          if (currentPath === '/register' || currentPath === '/register/') {
            log.log('On public page (register), skipping auth and store initialization');
            // Only initialize content store (for CMS translations), skip appStore
            contentStore.initialize().then(() => {
              window.dispatchEvent(new CustomEvent('contentStoreReady', { detail: { success: true } }));
            });
            return;
          }
        }

        // Check if this is a page reload
        const isPageReload = typeof window !== 'undefined' &&
          window.performance?.navigation?.type === 1;

        // Clear redirect loop counter for fresh start
        if (isPageReload) {
          clearRedirectLoopCounter();
        }

        // Check if user profile is cached during reload
        let cachedUserProfile = null;
        if (isPageReload) {
          try {
            const cachedProfileJson = sessionStorage.getItem('userProfileCache');
            if (cachedProfileJson) {
              cachedUserProfile = JSON.parse(cachedProfileJson);
            }
          } catch (e) {
            log.warn('Error reading cached profile:', e);
          }
        }

        // First check if we already have auth state
        const isAuthenticated = authService.getAuthState()?.authenticated || false;
        const hasProjectId = !!authService.getProjectId();

        // For reloads with cached profile, ensure profile is set in auth service
        if (isPageReload && cachedUserProfile) {
          log.log('Using cached user profile during page reload');

          // Ensure auth service has the user profile
          if (authService.state) {
            authService.state.authenticated = true;
            authService.state.userProfile = cachedUserProfile;
          }
        }

        // For reloads, trust cached projectId
        if (isPageReload && hasProjectId && !isAuthenticated) {
          log.log('Detected page reload with projectId but no auth - forcing auth state');

          // Force authentication state to true when projectId exists
          if (authService.state) {
            authService.state.authenticated = true;
          }

          // Initialize stores with auth information available
          initializeStores();
          return;
        }

        // If either authentication or projectId is missing, initialize auth
        if (!isAuthenticated || !hasProjectId) {
          await authService.initialize();
          log.log('Authentication service initialized');
        }

        // Now check the auth status again
        const authState = authService.getAuthState();
        log.log(`Authentication check completed: ${authState?.authenticated ? 'Authenticated' : 'Not authenticated'}`);

        if (authState?.authenticated) {
          // Initialize session timeout monitoring if authenticated
          if (authService.initSessionTimeout) {
            authService.initSessionTimeout();
            log.log('Session timeout monitoring initialized');
          }

          // Ensure we have project ID
          const projectId = authState.user?.projectId || '';
          log.log(`Project ID: ${projectId || 'Not available'}`);

          // Ensure user profile is fully loaded and cached
          if (authState.user) {
            // Cache user profile for persistence during page reloads
            if (typeof cacheUserProfile === 'function') {
              cacheUserProfile(authState.user);
            } else {
              try {
                // Fallback caching if function not available
                sessionStorage.setItem('userProfileCache', JSON.stringify(authState.user));
              } catch (e) {
                log.warn('Error caching user profile:', e);
              }
            }
          }
        }

        // Now initialize stores with auth information available
        initializeStores();
      } catch (error) {
        log.error('Error during auth check:', error);

        // Try to recover if we have cached projectId
        try {
          if (typeof sessionStorage !== 'undefined') {
            const cachedProfileJson = sessionStorage.getItem('userProfileCache');
            if (cachedProfileJson) {
              const cachedProfile = JSON.parse(cachedProfileJson);
              if (cachedProfile && cachedProfile.projectId) {
                log.log('Using cached profile after auth error');

                // Set minimal authenticated state
                if (authService.state) {
                  authService.state.authenticated = true;
                  authService.state.userProfile = cachedProfile;
                }

                // Continue with initialization
                initializeStores();
                return;
              }
            }
          }
        } catch (e) {
          log.warn('Error recovering with cached profile:', e);
        }

        // Still initialize stores even if auth check fails
        initializeStores();
      }
    };

    /**
     * Initialize content and app stores in parallel
     * @private
     */
    const initializeStores = () => {
      // Initialize content store with proper promise handling
      contentStore.initialize()
        .then(success => {
          contentInitialized = true;

          if (success) {
            log.log(`Content store initialized successfully (${Date.now() - startTime}ms)`);

            // Trigger a content refresh event after initialization
            window.dispatchEvent(new CustomEvent('contentStoreReady', {
              detail: { success: true }
            }));
          } else {
            // Content store is ready - the "false" return just means some optional content types
            // weren't found in GraphQL static queries, which is normal for pages that don't use them
            // Still trigger the event so components can proceed
            window.dispatchEvent(new CustomEvent('contentStoreReady', {
              detail: { success: true }
            }));
          }

          // Check if both stores are ready
          checkBothStoresInitialized();
        })
        .catch(error => {
          contentInitialized = true;
          log.error('Content store initialization error:', error);

          // Trigger event with error status so components can handle appropriately
          window.dispatchEvent(new CustomEvent('contentStoreReady', {
            detail: { success: false, error: error.message }
          }));

          // Check if both stores are ready
          checkBothStoresInitialized();
        });

      // Initialize app store in parallel
      appStore.initialize()
        .then(success => {
          appInitialized = true;

          if (success) {
            log.log(`App store initialized successfully (${Date.now() - startTime}ms)`);

            // Trigger app store ready event
            window.dispatchEvent(new CustomEvent('appStoreReady', {
              detail: { success: true }
            }));
          } else {
            log.warn('App store initialized with fallbacks, some data may be missing');

            window.dispatchEvent(new CustomEvent('appStoreReady', {
              detail: { success: false }
            }));
          }

          // Check if both stores are ready
          checkBothStoresInitialized();
        })
        .catch(error => {
          appInitialized = true;
          log.error('App store initialization error:', error);

          window.dispatchEvent(new CustomEvent('appStoreReady', {
            detail: { success: false, error: error.message }
          }));

          // Check if both stores are ready
          checkBothStoresInitialized();
        });
    };

    // Start auth check, then store initialization
    checkAuthFirst();

    // Set up cross-tab synchronization (login, logout, data refresh)
    crossTabManager.initialize();
  }
  // Handle tabs returning from Keycloak (auth.supplier-s.io or auth.ctrl-s.io)
  if (typeof window !== 'undefined') {
    const referrer = document.referrer;
    const isReturningFromAuth = isKnownAuthHost(referrer);

    if (isReturningFromAuth && window.location.pathname === '/logout/') {
      log.log('Tab returning from Keycloak to logout page');

      // Force a hard reload to ensure Gatsby resources are loaded
      // This is necessary because the tab left the app entirely
      if (!sessionStorage.getItem('reloadedAfterReturn')) {
        sessionStorage.setItem('reloadedAfterReturn', 'true');
        window.location.reload();
        return;
      }

      // Clear the flag after reload
      sessionStorage.removeItem('reloadedAfterReturn');
    }
  }
};

/**
 * Enhanced onRouteUpdate to support profile restoration during navigation
 * and route hydration on ALL route updates including reloads
 * 
 * @param {Object} params - Route information
 * @param {Object} params.location - Current location object
 * @param {Object} params.prevLocation - Previous location object
 */
export const onRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window !== 'undefined' && window.appStore && location) {
    const isReload = !prevLocation;

    // Handle hydration with appropriate timing based on load type
    if (isReload) {
      // Page reload/first load - wait for store initialization before hydrating
      const hydrateAfterInit = async () => {
        try {
          // Wait for store to be initialized
          // First login with population can take 30+ seconds, so we use a generous timeout
          // This is just for hydration - the store init itself shows its own progress
          const maxAttempts = 350; // 35 seconds max
          let attempts = 0;

          while (attempts < maxAttempts) {
            // Check if store is initialized
            if (window.appStore?.state?.initialized) {
              // Hydrate current route to ensure fresh data
              await window.appStore.hydrateForRoute(location.pathname);
              break;
            }

            // Wait 100ms before checking again
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }
          // No warning needed - if we timeout, the user already sees the store init in progress
        } catch (error) {
          log.error('Error during post-reload hydration:', error);
        }
      };

      // Start hydration process
      hydrateAfterInit();
    } else {
      // Normal navigation between routes - hydrate immediately
      setTimeout(() => {
        if (window.appStore.hydrateForRoute) {
          window.appStore.hydrateForRoute(location.pathname);
        }
      }, 100);
    }

    // Restore user profile if needed (existing functionality)
    if (!isReload) {
      setTimeout(() => {
        try {
          const authState = authService.getAuthState();

          // If authenticated but missing user profile, restore from cache
          if (authState?.authenticated && !authState?.user?.name) {
            const cachedProfileJson = sessionStorage.getItem('userProfileCache');
            if (cachedProfileJson) {
              const cachedProfile = JSON.parse(cachedProfileJson);
              if (cachedProfile && authService.state) {
                log.log('Restoring user profile during navigation');
                authService.state.userProfile = cachedProfile;
              }
            }
          }
        } catch (e) {
          log.warn('Error restoring profile during navigation:', e);
        }
      }, 200);

      // Track page navigation with PostHog (if available and consented)
      if (typeof window !== 'undefined' && window.postHogService) {
        setTimeout(() => {
          window.postHogService.trackPageView(location.pathname + location.search);
        }, 100);
      }
    }
  }
};

/**
 * Routes that require authentication and use the sidebar layout
 */
const protectedRoutes = [
  "/overview",
  "/self-assessment",
  "/carbon-accounting",
  // Rebrand (#885): /supplier-engagement is the new canonical URL for the
  // Supplier Engagement (formerly Supply Chain Management) module. Both
  // paths stay protected forever so existing deep-links keep working.
  "/supplier-engagement",
  "/supply-chain-management",
  "/platform",
  "/account",
  "/first-run",
  "/testings",
];

/**
 * Protected routes that use LayoutDefault (no sidebar) instead of LayoutSidebar
 */
const protectedFullscreenRoutes = [];

/**
 * DashboardLoadingOverlay — kept as a no-op.
 * Loading is now rendered by the index page itself inside LayoutDefault.
 */
const DashboardLoadingOverlay = () => null;

/**
 * PageWrapper component that determines which layout to use based on the route.
 * Now includes PostHog analytics provider.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.element - Page element to wrap
 * @param {Object} props.props - Page props with location information
 * @returns {React.ReactNode} Wrapped page element with appropriate layout
 */
const PageWrapper = ({ element, props }) => {
  // Global dashboard loading state - for smooth transition from index to sidebar pages
  // IMPORTANT: Initialize to false for SSR/hydration consistency, then check sessionStorage in useEffect
  // This prevents React hydration error #418 (server/client mismatch)
  const [showDashboardLoading, setShowDashboardLoading] = React.useState(false);

  // Check loading flag after hydration to avoid SSR mismatch
  React.useEffect(() => {
    // Initial check on mount
    const isActive = sessionStorage.getItem('dashboardLoadingActive') === 'true';
    if (isActive) {
      setShowDashboardLoading(true);
    }

    // Listen for changes to the loading flag
    const checkLoadingFlag = () => {
      const active = sessionStorage.getItem('dashboardLoadingActive') === 'true';
      setShowDashboardLoading(active);
    };

    window.addEventListener('storage', checkLoadingFlag);
    return () => window.removeEventListener('storage', checkLoadingFlag);
  }, []);

  // Expose function to hide loading (called by overview page when ready)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.hideDashboardLoading = () => {
        sessionStorage.removeItem('dashboardLoadingActive');
        setShowDashboardLoading(false);
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.hideDashboardLoading;
      }
    };
  }, []);

  // Calculate layout type FIRST (before any hooks)
  const errorPages = ['/404', '/500', '/error'];
  const isErrorPage = errorPages.includes(props.location.pathname);
  const isLogoutPage = props.location.pathname === '/logout' || props.location.pathname === '/logout/';
  const isPrototypePage = props.location.pathname.startsWith('/prototypes/');

  const isFullscreenRoute = protectedFullscreenRoutes.some((route) =>
    props.location.pathname.startsWith(route)
  );

  const isSidebarLayout = !isErrorPage &&
    !isLogoutPage &&
    !isFullscreenRoute &&
    protectedRoutes.some((route) =>
      props.location.pathname.startsWith(route)
    );

  const requiresAuth = !isLogoutPage &&
    protectedRoutes.some(route =>
      props.location.pathname.startsWith(route)
    );

  // SSR-safe: Use props.location.search which is available during SSR
  // This avoids hydration mismatch from typeof window !== 'undefined' check
  const searchParams = props.location?.search || '';
  const hasTokenParam = searchParams.includes('token=') ||
    searchParams.includes('invitationToken=') ||
    searchParams.includes('invitation=');

  // Add/remove 'sidebar-layout' class to body based on layout type
  // MUST be before any early returns (React Hooks rules)
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isSidebarLayout) {
        document.body.classList.add('sidebar-layout');
        log.debug('Added sidebar-layout class, banner should be visible');
      } else {
        document.body.classList.remove('sidebar-layout');
        log.debug('Removed sidebar-layout class, banner should be hidden');
      }
    }

    // Cleanup on unmount
    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('sidebar-layout');
      }
    };
  }, [isSidebarLayout]);

  // Restore user profile on mount (hydration handled by onRouteUpdate)
  React.useEffect(() => {
    if (typeof window === 'undefined' || isLogoutPage) return;

    // Ensure user profile is available from cache
    try {
      const authState = authService.getAuthState();
      if (authState?.authenticated && (!authState.user || !authState.user.name)) {
        const cachedProfileJson = sessionStorage.getItem('userProfileCache');
        if (cachedProfileJson) {
          const cachedProfile = JSON.parse(cachedProfileJson);
          if (cachedProfile && authService.state) {
            authService.state.userProfile = cachedProfile;
          }
        }
      }
    } catch (e) {
      // Silent in production
    }
    // NOTE: Hydration is handled by onRouteUpdate - no duplicate call here
  }, [props.location, isLogoutPage]);

  // Bypass AuthRouter completely for logout page (no PostHog here)
  if (isLogoutPage) {
    return (
      <MotionConfig>
        <StoreStatusProvider>
          <LanguageProvider>
            <LayoutDefault>
              {element}
            </LayoutDefault>
          </LanguageProvider>
        </StoreStatusProvider>
      </MotionConfig>
    );
  }

  // Enhanced with StoreStatusProvider, PostHogProvider, and IntercomProvider
  return (
    <MotionConfig>
      <StoreStatusProvider>
        <LanguageProvider>
          <PostHogProvider>
            <AuthRouter requireAuth={requiresAuth}>
              {hasTokenParam ? (
                // For token pages, don't add additional layout - AuthRouter handles it
                <>
                  {element}
                </>
              ) : isSidebarLayout ? (
                <LayoutSidebar location={props.location}>
                  {element}
                </LayoutSidebar>
              ) : props.location.pathname === '/register' ? (
                <>
                  {element}
                </>
              ) : isPrototypePage ? (
                // Prototype pages manage their own layout
                <>
                  {element}
                </>
              ) : (
                <LayoutDefault>
                  <>
                    {element}
                  </>
                </LayoutDefault>
              )}
              {/*
                Rebrand-cleanup gates (rebrand-cleanup-canonical-plan.md):
                  - TermsAcceptanceGate fires for any authenticated user who
                    has not yet accepted the current terms version. Blocking
                    until accepted; legal first.
                  - AnnouncementHost paints the per-user announcement queue
                    (welcome message, future broadcasts). Defers paint while
                    the gate is open via window.__termsGateOpen — marketing
                    second.
                Both render only when requiresAuth so they never appear on
                public/login routes.
              */}
              {requiresAuth && !hasTokenParam && (
                <>
                  <TermsAcceptanceGate />
                  <AnnouncementHost />
                </>
              )}
            </AuthRouter>
            {/* Global dashboard loading overlay with exit animation */}
            <DashboardLoadingOverlay show={showDashboardLoading} />
          </PostHogProvider>
        </LanguageProvider>
      </StoreStatusProvider>
    </MotionConfig>
  );
};

/**
 * Wrap page elements with the PageWrapper
 *
 * @param {Object} options - Wrapper options
 * @param {React.ReactNode} options.element - Page element to wrap
 * @param {Object} options.props - Page props
 * @returns {React.ReactNode} Wrapped page element
 */
export const wrapPageElement = ({ element, props }) => (
  <PageWrapper element={element} props={props} />
);

/**
 * Scroll all potential scroll containers to a position
 * Handles both window scroll and internal layout scroll containers
 * Uses setTimeout to defer scroll after DOM mutations (Gatsby 5 hydration fix)
 *
 * @param {number} x - X position to scroll to
 * @param {number} y - Y position to scroll to
 */
const scrollToPosition = (x = 0, y = 0) => {
  // Use setTimeout to defer scroll - this is critical for Gatsby 5
  // The delay ensures scroll happens after DOM hydration/mutations complete
  // See: https://github.com/gatsbyjs/gatsby/issues/38201
  setTimeout(() => {
    // Scroll window
    window.scrollTo(x, y);

    // Scroll the main layout content container (has overflow-auto)
    // This is the actual scroll container in the sidebar layout
    const layoutContent = document.querySelector('main.overflow-auto');
    if (layoutContent) {
      layoutContent.scrollTo(x, y);
    }
  }, 0);
};

/**
 * Control scroll behavior on route changes
 *
 * Gatsby 5 has a known issue where scroll-to-top gets interrupted by DOM
 * hydration. The fix is to use setTimeout to defer the scroll action.
 * See: https://github.com/gatsbyjs/gatsby/issues/38201
 *
 * @param {Object} args - Scroll update arguments
 * @param {Object} args.routerProps - Router props with location info
 * @param {Object} args.prevRouterProps - Previous router props
 * @param {Function} args.getSavedScrollPosition - Get saved scroll position for location
 * @returns {boolean} Always returns false - we handle scrolling ourselves
 */
export const shouldUpdateScroll = ({ routerProps, getSavedScrollPosition }) => {
  const { location } = routerProps;

  // For hash links (e.g., /page#section), let browser handle it
  if (location.hash) {
    return true;
  }

  // Get saved position for back/forward navigation
  const savedPosition = getSavedScrollPosition(location);

  // Scroll to saved position or top (deferred with setTimeout)
  if (savedPosition) {
    scrollToPosition(savedPosition[0], savedPosition[1]);
  } else {
    scrollToPosition(0, 0);
  }

  // Return false - we handle scrolling ourselves
  return false;
};